// La instrucción adicional de Automática detrás de un interruptor —
// ADR-2026-10-15-la-instruccion-de-automatico-se-despliega-con-un-interruptor.
//
// Apagado se ve sólo el renglón "Instrucciones adicionales" con su switch; prendido
// despliega el MISMO campo de siempre (`CampoInstruccion`, con "Recientes") ADENTRO de
// la misma caja, con una animación corta de alto y opacidad
// (ADR-2026-10-16-la-instruccion-se-despliega-dentro-de-su-caja). Lo montan las dos
// pantallas de opciones del modo: el paso 2 de desktop (`PanelAutomaticoCompacto`) y la
// segunda pantalla de mobile (`PresetDetailSheet`).
//
// Apagar NO esconde el texto: lo SACA de la generación (`onChange('')`). Una indicación
// que viaja al prompt sin que se vea en la pantalla sería una tanda que el usuario no
// puede explicar. Lo escrito se guarda acá y vuelve si se prende de nuevo.

import React from 'react';
import { CampoInstruccion } from './AjustesRapidosProducto';

export const InstruccionConInterruptor: React.FC<{
  valor: string;
  onChange: (v: string) => void;
  /** Alto del campo en renglones — ver `CampoInstruccion`. */
  filas?: number;
}> = ({ valor, onChange, filas = 3 }) => {
  // Arranca prendido si ya hay algo escrito: volver de un resultado, "Seguir generando"
  // o una instrucción que puso el agente tienen que verse.
  const [activo, setActivo] = React.useState(() => valor.trim() !== '');
  /** Lo que había escrito al apagar, para devolverlo si se vuelve a prender. */
  const guardadoRef = React.useRef('');
  /** El tap del usuario enfoca el campo; abrirse solo (por el agente) no. */
  const enfocarRef = React.useRef(false);
  const campoRef = React.useRef<HTMLDivElement>(null);

  // Si el texto llega de AFUERA con el interruptor apagado —el agente, el restore—, se
  // prende: un valor que se manda tiene que estar a la vista.
  React.useEffect(() => {
    if (!activo && valor.trim() !== '') setActivo(true);
  }, [valor, activo]);

  // El campo queda MONTADO aunque esté cerrado —es lo que deja animar la salida—, así
  // que cerrado se vuelve `inert`: ni el tab ni un lector de pantalla entran a un campo
  // de alto cero. Por la propiedad del DOM y no por el atributo de JSX: React 18 no
  // conoce `inert`. Antes del foco, en el mismo efecto, para no enfocar algo inerte.
  React.useLayoutEffect(() => {
    const el = campoRef.current;
    if (!el) return;
    el.inert = !activo;
    if (activo && enfocarRef.current) {
      enfocarRef.current = false;
      el.querySelector('textarea')?.focus({ preventScroll: true });
    }
  }, [activo]);

  const alternar = () => {
    if (activo) {
      guardadoRef.current = valor;
      setActivo(false);
      if (valor !== '') onChange('');
      return;
    }
    enfocarRef.current = true;
    setActivo(true);
    if (valor === '' && guardadoRef.current !== '') onChange(guardadoRef.current);
  };

  return (
    // UNA caja para el renglón y el campo: el campo se lee como lo que el interruptor
    // abre, no como una pieza suelta debajo. Radios concéntricos: 16 afuera = 8 del campo
    // + 8 de aire (`p-2` del contenido).
    <div className="rounded-2xl bg-black/[0.035]">
      <button
        type="button"
        role="switch"
        aria-checked={activo}
        onClick={alternar}
        className="w-full flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-left"
      >
        <span className="text-[14.5px] font-medium text-brand-black/90">
          Instrucciones adicionales
        </span>
        {/* El mismo interruptor que `SwitchSeleccionMultiple`. */}
        <span
          aria-hidden="true"
          className={`shrink-0 w-9 h-5 rounded-full p-0.5 transition-colors ${
            activo ? 'bg-brand-primary' : 'bg-black/15'
          }`}
        >
          <span
            className={`block w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
              activo ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </span>
      </button>
      {/* Se despliega animando el alto con `grid-template-rows` (0fr ↔ 1fr), el mismo
          recurso que usa `FondoPicker`, más un fundido. Sutil: 250ms y sin desplazamiento. */}
      <div
        className={`grid transition-[grid-template-rows,opacity] duration-[250ms] ease-out ${
          activo ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div ref={campoRef} className="min-h-0 overflow-hidden">
          {/* El campo, blanco sobre el gris de la caja y con el radio que le toca adentro.
              Se pisa desde afuera para no tocar `CampoInstruccion`, que sigue igual en sus
              otros usos. */}
          <div className="px-2 pb-2 [&_textarea]:bg-white [&_textarea]:rounded-lg">
            <CampoInstruccion valor={valor} onChange={onChange} filas={filas} />
          </div>
        </div>
      </div>
    </div>
  );
};
